import { config } from "@repo/config";
import { pageHead, siteOrigin } from "@shared/lib/meta";
import { cn } from "@ui/lib";
import type { ReactNode } from "react";
import { WHATSAPP_URL } from "../site";
import { Chevron, mkButton, mkLink } from "./MkButton";

/**
 * The parts every page is made of. A page is an intro and a few sections, one idea each; the
 * tone of a section (white, mist, night) is how pages get their rhythm, not boxes and borders.
 */

type Tone = "white" | "mist" | "night";

const TONE: Record<Tone, string> = {
	white: "bg-(--mk-white)",
	mist: "bg-(--mk-mist)",
	night: "bg-(--mk-night) text-(--mk-on-night)",
};

/** A page's first screen: what it is, in one line, and the product shown large below. */
export function PageIntro({
	kicker,
	title,
	lead,
	children,
}: {
	kicker: string;
	title: string;
	lead: string;
	children?: ReactNode;
}) {
	return (
		<section aria-labelledby="page-title" className="overflow-hidden">
			<div className="mk-container pt-16 text-center sm:pt-24">
				<p className="mk-kicker text-(--mk-ink-2)">{kicker}</p>
				<h1 id="page-title" className="mk-h1 mx-auto mt-3 max-w-4xl">
					{title}
				</h1>
				<p className="mk-lead mx-auto mt-5 max-w-2xl text-(--mk-ink-2)">
					{lead}
				</p>
				<div className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-3">
					<a href="/demo" className={mkButton()}>
						Book a demo
					</a>
					<a
						href={WHATSAPP_URL}
						target="_blank"
						rel="noopener"
						className={mkLink()}
					>
						Talk to us on WhatsApp
						<Chevron />
					</a>
				</div>
			</div>
			{children ? (
				<div className="mk-container mt-14 sm:mt-20">
					<div className="mk-rise overflow-hidden rounded-[32px] bg-(--mk-mist) px-4 pt-10 sm:px-10 sm:pt-14">
						{children}
					</div>
				</div>
			) : null}
		</section>
	);
}

/** A section with a headline (and an optional line under it) over its content. */
export function Section({
	id,
	title,
	lead,
	tone = "white",
	center,
	children,
}: {
	id: string;
	title: string;
	lead?: string;
	tone?: Tone;
	center?: boolean;
	children?: ReactNode;
}) {
	return (
		<section aria-labelledby={id} className={cn("mk-section", TONE[tone])}>
			<div className="mk-container">
				<div
					className={cn(
						"mk-reveal max-w-3xl",
						center && "mx-auto text-center",
					)}
				>
					<h2 id={id} className="mk-h2">
						{title}
					</h2>
					{lead ? (
						<p
							className={cn(
								"mk-lead mt-5",
								tone === "night"
									? "text-(--mk-on-night-muted)"
									: "text-(--mk-ink-2)",
							)}
						>
							{lead}
						</p>
					) : null}
				</div>
				{children ? (
					<div className="mt-12 sm:mt-16">{children}</div>
				) : null}
			</div>
		</section>
	);
}

export interface Detail {
	title: string;
	text: string;
}

/** Facts side by side, each under a hairline: the reading list of a section. */
export function DetailGrid({
	items,
	columns = 3,
	tone = "white",
}: {
	items: Detail[];
	columns?: 2 | 3 | 4;
	tone?: Tone;
}) {
	return (
		<dl
			className={cn(
				"grid gap-x-10 gap-y-10 sm:grid-cols-2",
				columns === 3 && "lg:grid-cols-3",
				columns === 4 && "lg:grid-cols-4",
			)}
		>
			{items.map((item) => (
				<div
					key={item.title}
					className={cn(
						"mk-reveal border-t pt-5",
						tone === "night"
							? "border-(--mk-night-3)"
							: "border-(--mk-line)",
					)}
				>
					<dt className="font-semibold text-[19px] tracking-[-0.02em]">
						{item.title}
					</dt>
					<dd
						className={cn(
							"mt-2 text-[16px] leading-relaxed",
							tone === "night"
								? "text-(--mk-on-night-muted)"
								: "text-(--mk-muted)",
						)}
					>
						{item.text}
					</dd>
				</div>
			))}
		</dl>
	);
}

/** Text on one side, the product on the other, inside a section. */
export function SplitRow({
	title,
	text,
	points,
	visual,
	reverse,
	id,
}: {
	id?: string;
	title: string;
	text: string;
	points?: string[];
	visual: ReactNode;
	reverse?: boolean;
}) {
	return (
		<div
			id={id}
			className="grid scroll-mt-20 items-center gap-10 lg:grid-cols-2 lg:gap-20"
		>
			<div className={cn("mk-reveal", reverse && "lg:order-2")}>
				<h3 className="mk-h3">{title}</h3>
				<p className="mt-3 text-(--mk-ink-2) text-[17px] leading-relaxed">
					{text}
				</p>
				{points?.length ? (
					<ul className="mt-6 space-y-2.5 text-(--mk-ink-2) text-[16px]">
						{points.map((point) => (
							<li key={point} className="flex gap-3">
								<span
									aria-hidden
									className="mt-[0.6em] size-1.5 shrink-0 rounded-full bg-(--mk-signal)"
								/>
								{point}
							</li>
						))}
					</ul>
				) : null}
			</div>
			<div
				className={cn(
					"mk-rise overflow-hidden rounded-[28px] bg-(--mk-mist) px-4 pt-8 sm:px-8 sm:pt-10",
					reverse && "lg:order-1",
				)}
			>
				{visual}
			</div>
		</div>
	);
}

/** Numbered steps in a row: how something happens, in order. */
export function Steps({ items }: { items: Detail[] }) {
	return (
		<ol className="grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
			{items.map((item, i) => (
				<li key={item.title} className="mk-reveal">
					<span className="font-semibold text-(--mk-faint) text-5xl tracking-[-0.04em] tabular-nums">
						{i + 1}
					</span>
					<h3 className="mt-4 font-semibold text-[19px] tracking-[-0.02em]">
						{item.title}
					</h3>
					<p className="mt-2 text-(--mk-muted) text-[16px] leading-relaxed">
						{item.text}
					</p>
				</li>
			))}
		</ol>
	);
}

/** Bumped when the cards are re-rendered, so link previews refetch them past platform caches. */
const SHARE_CARD_VERSION = 2;

/**
 * The page's own link-preview card, rendered by apps/web/brand/og-cards.mjs into
 * public/og/<path with dashes>.png: a new page adds its card there.
 */
function shareCardFor(path: string, title: string) {
	const slug =
		path === "/"
			? "home"
			: path.startsWith("/compare/")
				? "compare"
				: path.slice(1).replaceAll("/", "-");
	return {
		url: `${siteOrigin}/og/${slug}.png?v=${SHARE_CARD_VERSION}`,
		alt: `${title.replace(/ \| Hephon Commerce$/, "")}: a shopper's request and the products the assistant added.`,
	};
}

/**
 * The product itself, on every public page: what it is, where it is sold, in which languages,
 * and who makes it (the full Organization lives on hephon.ai).
 */
export const SOFTWARE_LD = {
	"@type": "SoftwareApplication",
	"@id": `${siteOrigin}/#software`,
	name: config.appName,
	applicationCategory: "BusinessApplication",
	applicationSubCategory: "AI shopping assistant",
	operatingSystem: "Web, iOS, Android",
	areaServed: [
		{ "@type": "Country", name: "Saudi Arabia" },
		{ "@type": "Country", name: "United Arab Emirates" },
		{ "@type": "Country", name: "Lebanon" },
	],
	availableLanguage: ["ar", "en", "fr"],
	description:
		"AI shopping assistant for supermarkets and grocery retailers in Saudi Arabia and the UAE. Shoppers ask in Arabic or English, in the retailer's app, on WhatsApp or by phone; it searches the live catalog with each branch's prices, fills the basket and orders only after the shopper confirms.",
	url: `${siteOrigin}/`,
	publisher: { "@id": "https://hephon.ai/#organization" },
	audience: {
		"@type": "BusinessAudience",
		audienceType: "Supermarkets, grocery delivery and ecommerce retailers",
	},
	featureList: [
		"AI shopping agent that searches the retailer's live catalog",
		"Arabic chatbot for Saudi and Gulf Arabic, with Lebanese, Arabizi, English and French",
		"WhatsApp shopping, ordering and checkout",
		"AI voice ordering by phone and WhatsApp calls",
		"Recipes turned into baskets with pack sizes",
		"SDK for iOS, Android and web apps",
	],
};

export interface FaqItem {
	q: string;
	a: string;
}

/**
 * A marketing page's head: title, description, canonical, indexing, and structured data (the
 * page's breadcrumb, its FAQ, anything else it passes).
 */
export function marketingHead({
	title,
	description,
	path,
	breadcrumb,
	faq,
	jsonLd = [],
	updated,
}: {
	title: string;
	description: string;
	path: string;
	breadcrumb?: string[];
	faq?: FaqItem[];
	jsonLd?: object[];
	/** When the page's facts were last checked (YYYY-MM-DD); the page shows the same date. */
	updated?: string;
}) {
	const head = pageHead({
		title,
		description,
		path,
		image: shareCardFor(path, title),
	});
	const graph: object[] = [
		{
			"@type": "WebPage",
			"@id": `${siteOrigin}${path}#webpage`,
			url: `${siteOrigin}${path}`,
			name: title,
			description,
			inLanguage: "en",
			isPartOf: { "@id": `${siteOrigin}/#website` },
			about: { "@id": `${siteOrigin}/#software` },
			publisher: { "@id": "https://hephon.ai/#organization" },
			...(updated ? { dateModified: updated } : {}),
		},
		SOFTWARE_LD,
		...jsonLd,
	];
	if (breadcrumb?.length) {
		const segments = path.split("/").filter(Boolean);
		graph.push({
			"@type": "BreadcrumbList",
			itemListElement: [
				{
					"@type": "ListItem",
					position: 1,
					name: "Home",
					item: `${siteOrigin}/`,
				},
				...breadcrumb.map((name, i) => ({
					"@type": "ListItem",
					position: i + 2,
					name,
					// The last crumb is this page; earlier ones are its parents.
					item:
						i === breadcrumb.length - 1
							? `${siteOrigin}${path}`
							: `${siteOrigin}/${segments.slice(0, i + 1).join("/")}`,
				})),
			],
		});
	}
	if (faq?.length) {
		graph.push({
			"@type": "FAQPage",
			mainEntity: faq.map((item) => ({
				"@type": "Question",
				name: item.q,
				acceptedAnswer: { "@type": "Answer", text: item.a },
			})),
		});
	}
	return {
		...head,
		meta: [
			...head.meta,
			{
				name: "robots",
				content: "index, follow, max-image-preview:large",
			},
		],
		scripts: [
			{
				type: "application/ld+json",
				children: JSON.stringify({
					"@context": "https://schema.org",
					"@graph": graph,
				}),
			},
		],
	};
}
